import { JSX } from "react";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { SerendieSymbol } from "@serendie/symbols";
import AddOntologyNodeDialog from "./AddOntologyNodeDialog";
import { IOntologyNode as OntologyNode } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { Guid } from "@iconics/core";
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons } from "@iconics/components";

export default class OntologyNodesCard extends IbssComponent<IProps, IState>
{
    constructor(props: IProps)
    {
        super(props);
        const { localStorageProvider } = appContext();

        this.state = {
            userCanDeleteOntologyNodes: localStorageProvider.hasRight('DATAMODEL.OntologyNodes.Delete'),
            userCanAddOntologyNodes: localStorageProvider.hasRight('DATAMODEL.OntologyNodes.Create'),
            gridData: { rows: [], checkedIds: [] },
            gridRefreshToken: Guid.new().toString(),
            showAddNodeDialog: false,
        };
    }

    public componentDidUpdate(prevProps: Readonly<IProps>): void
    {
        if (prevProps.nodeId !== this.props.nodeId)
        {
            this.refreshGrid();
        }
    }

    private get checkedIds(): string[]
    {
        return this.state.gridData.checkedIds ?? [];
    }

    private refreshGrid(): void
    {
        this.setState({ gridRefreshToken: Guid.new().toString() });
    }

    private async handleDeleteClick(): Promise<void>
    {
        await this.loading(async () =>
        {
            for (const id of this.checkedIds)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.byOntologyNodeId.delete({
                    tenancy: appContext().tenantId,
                    nodeId: this.props.nodeId,
                    ontologyNodeId: id
                });
            }
            this.refreshGrid();
        });
    }

    private handleRowAdded(): void
    {
        this.setState({ showAddNodeDialog: false });
        this.refreshGrid();
    }

    private async handleRowsRequest(request: IGridRowsRequest<IOntologyNode>): Promise<IGridRowsResponse<IOntologyNode>>
    {
        if (!this.props.nodeId)
            return { rows: [], nextSkipToken: '' };
        
        const ontologyNodeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.get<PagedResponse<OntologyNode[]>>({
            tenancy: appContext().tenantId,
            nodeId: this.props.nodeId,
        });

        const ontologyNodes: IOntologyNode[] = ontologyNodeData.value
            .map(x => ({
                id: x.OntologyNode_ID,
                name: x.OntologyNode_Name,
                ontologyName: x.Ontology_Name,
                ontologyNodeName: x.OntologyNode_Name
            }));

        this.props.onOntologyNodesLoaded(ontologyNodes);

        return {
            rows: DataGrid.sort(ontologyNodes, request.sorter),
            nextSkipToken: '',
        };
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <>
                <Card
                    title={labels.Platform_Node_OntologyNodeListTitle}
                    subTitle={labels.Platform_Node_OntologyNodeListSubTitle}
                    onSectionUpdate={i => this.props.onSectionUpdate(i)}
                >
                    <GridContainer>
                        <GridItem>
                            <ButtonBar>
                                <RightButtons>
                                    <DeleteButton
                                        small={true}
                                        disabled={!this.state.userCanDeleteOntologyNodes || this.checkedIds.length === 0}
                                        dialogMessage={labels.Platform_NodeOntologyNode_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDeleteClick()}
                                    />
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='plus' />}
                                        onClick={() => this.setState({ showAddNodeDialog: true })}
                                        disabled={!this.state.userCanAddOntologyNodes || this.checkedIds.length > 0}
                                    >
                                        {labels.Platform_CommonAction_Add}
                                    </Button>
                                </RightButtons>
                            </ButtonBar>
                        </GridItem>
                        <GridItem>
                            <DataGrid
                                columns={this.props.size === 'small'
                                    ? [
                                        {
                                            columnId: Helper.nameOf<IOntologyNode>("name"),
                                            title: labels.OntologyNode_Name,
                                            columnWidth: 100,
                                            sortable: true,
                                            getCellValue: i => i.name,
                                        },
                                    ]
                                    : [
                                        {
                                            columnId: Helper.nameOf<IOntologyNode>("name"),
                                            title: labels.OntologyNode_Name,
                                            columnWidth: 50,
                                            sortable: true,
                                            getCellValue: i => i.name,
                                        },
                                        {
                                            columnId: Helper.nameOf<IOntologyNode>("ontologyName"),
                                            title: labels.Ontology_Name,
                                            columnWidth: 50,
                                            sortable: true,
                                            getCellValue: i => i.ontologyName,
                                        },
                                    ]}
                                data={this.state.gridData}
                                onChange={i => this.setState({ gridData: i })}
                                rowId={i => i.id}
                                onRowsRequest={i => this.handleRowsRequest(i)}
                                refreshToken={this.state.gridRefreshToken}
                                showCheckBoxes={true}
                            />
                        </GridItem>
                    </GridContainer>
                </Card>
                <AddOntologyNodeDialog
                    open={this.state.showAddNodeDialog}
                    onClose={() => this.setState({ showAddNodeDialog: false })}
                    onSave={() => this.handleRowAdded()}
                    nodeId={this.props.nodeId}
                />
            </>
        );
    }
}

export interface IProps
{
    nodeId: number;
    onOntologyNodesLoaded: (nodes: IOntologyNode[]) => void;
    onSectionUpdate: OnSectionUpdateHandler;
    size?: 'small' | 'normal';
}

export interface IState
{
    userCanDeleteOntologyNodes: boolean;
    userCanAddOntologyNodes: boolean;
    gridData: IGridData<IOntologyNode>;
    gridRefreshToken: string;
    showAddNodeDialog: boolean;
}

export interface IOntologyNode
{
    id: string;
    ontologyName: string;
    ontologyNodeName: string;
    name: string;
}
